import { Navigate, Route, Routes } from 'react-router-dom';
import { AccountPage } from './pages/account';
import { MembershipAndBilling } from './pages/account/MembershipAndBilling';
import { Profile } from './pages/account/Profile';
import { Provider } from './pages/account/Provider';
import { AccountDeletion } from './pages/account/AccountDeletion';
import { AccountDeletionConfirmation } from './pages/account/AccountDeletionConfirmation';
import { HomePage } from './pages/HomePage';
import { SignOutPage } from './pages/SignOutPage';
import { DocumentsPage } from './pages/documents/DocumentsPage';
import { ClientIntakeQuestionnairePage } from './pages/ClientIntakeQuestionnairePage';
import { ConsentForm } from './pages/documents/ConsentForm';
import { HipaaForm } from './pages/documents/HipaaForm';
import { ViewDocumentPage } from './pages/documents/ViewDocumentPage';
import { ScreeningQuestionnairePage } from './pages/ScreeningQuestionnairePage';
import { GetCare, LocationSelection, SessionScheduler } from './pages/GetCarePage';
import { MoodTracking } from './pages/therapy/progress/MoodTracking';
import { Response } from './pages/health-record/Response';
import { Responses } from './pages/health-record/Responses';
import { Journal } from './pages/self-care/Journal';
import { AppointmentsPage } from './pages/AppointmentsPage';
import { AppointmentConfirmation } from './pages/AppointmentConfirmation';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { DocumentsIndex } from './pages/documents/DocumentsIndex';
import { ReflectionGuidePage } from './pages/ReflectionGuidePage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import HowItWorksPage from './pages/HowItWorksPage';
import BenefitsPage from './pages/BenefitsPage';
import FAQPage from './pages/FAQPage';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import Cookies from './components/Cookies';
import DataProtection from './components/DataProtection';
import AIEthics from './components/AIEthics';

import type { JSX } from "react";

interface RouterProps {
  navbarState?: {
    opened: boolean;
    setOpened: (value: boolean) => void;
  };
}

export function Router({ navbarState }: RouterProps): JSX.Element {
  return (
    <Routes>
    {/*  <Route path="/" element={<HomePage />} />*/}
      <Route path="/" element={<Navigate replace to="/reflection" />} />
      
      {/* Appointments */}
      <Route path="appointments" element={<AppointmentsPage />} />
      <Route path="appointments/confirmation" element={<AppointmentConfirmation />} />
      
      {/* Documents & Forms */}
      <Route path="documents" element={<DocumentsPage />}>
        <Route index element={<DocumentsIndex />} />
        
        {/* Forms */}
        <Route path="forms">
          <Route path="intake" element={<ClientIntakeQuestionnairePage />} />
          <Route path="consent" element={<ConsentForm />} />
          <Route path="screening" element={<ScreeningQuestionnairePage />} />
          <Route path="view" element={<ViewDocumentPage />} />
          <Route path="hipaa" element={<HipaaForm />} />
          <Route path="agreements" element={<ConsentForm />} />
          <Route path="profile" element={<Navigate to="/account/profile" replace />} />
        </Route>

        {/* Session Notes */}
        <Route path="notes" element={<Responses />} />
        <Route path="notes/:responseId" element={<Response />} />
      </Route>

      {/* Schedule Session */}
      <Route path="get-care" element={<GetCare />} />
      <Route path="get-care/location/:sessionType" element={<LocationSelection />} />
      <Route path="get-care/schedule/:sessionType/:locationId" element={<SessionScheduler />} />

      {/* Self Care */}
      <Route path="self-care">
        <Route path="mood" element={<MoodTracking />} />
        <Route path="journal" element={<Journal />} />
      </Route>

      {/* Reflection Guide */}
      <Route path="reflection" element={<ReflectionGuidePage navbarState={navbarState} />} />

      {/* Account Settings */}
      <Route path="account/*" element={<AccountPage />}>
        <Route index element={<Navigate replace to="/account/profile" />} />
        <Route path="profile" element={<Profile />} />
        <Route path="provider/*" element={<Provider />} />
        <Route path="membership-and-billing" element={<MembershipAndBilling />} />
        <Route path="delete-account" element={<AccountDeletion />} />
      </Route>
      
      {/* Account Deletion Confirmation */}
      <Route path="account-deletion-confirmation" element={<AccountDeletionConfirmation />} />
      
      {/* Settings */}
      <Route path="settings" element={<Navigate replace to="/account/profile" />} />
      <Route path="settings/subscription" element={<SubscriptionPage />} />

      <Route path="signout" element={<SignOutPage />} />
      
      {/* Password Reset */}
      <Route path="resetpassword" element={<ResetPasswordPage />} />
      <Route path="setpassword/:id/:secret" element={<SetPasswordPage />} />
      
      {/* Marketing Pages */}
      <Route path="how-it-works" element={<HowItWorksPage />} />
      <Route path="benefits" element={<BenefitsPage />} />
      <Route path="faq" element={<FAQPage />} />
      
      {/* Legal Pages */}
      <Route path="terms" element={<Terms />} />
      <Route path="privacy" element={<Privacy />} />
      <Route path="cookies" element={<Cookies />} />
      <Route path="data-protection" element={<DataProtection />} />
      <Route path="ai-ethics" element={<AIEthics />} />
    </Routes>
  );
}